/**
 * Wraps Firebase `httpsCallable` for AI-related cloud functions to support
 * local test mode.
 *
 * When VITE_MOCK_AI_REQUESTS is enabled, AI-related cloud function calls
 * return mock results. Non-AI functions (credit, user, etc.) are unaffected.
 */

import {
  httpsCallable,
  HttpsCallableOptions,
  Functions,
} from "firebase/functions";
import { buildMockCloudFunctionResult } from "./mockImageGenerator.js";
import { mapFirebaseError } from "../utils/appError";

const TEST_MODE = import.meta.env.VITE_MOCK_AI_REQUESTS === "true";

/** Cloud function names that should be mocked in test mode. */
const MOCK_FUNCTION_NAMES = new Set([
  "generateImageViaOpenRouter",
  "generateImageViaBriaProductShot",
]);

/**
 * Drop-in replacement for `httpsCallable` from `firebase/functions`.
 * In test mode, AI-related functions return mock results; everything else
 * delegates to the real SDK.
 */
export function getCallableFunction<TReq = unknown, TRes = unknown>(
  functionsInstance: Functions,
  name: string,
  options?: HttpsCallableOptions
) {
  if (TEST_MODE && MOCK_FUNCTION_NAMES.has(name)) {
    return createMockCallable<TReq, TRes>(name);
  }
  const fn = httpsCallable<TReq, TRes>(functionsInstance, name, options);
  // Wrap the callable to map Firebase errors to AppError
  return async (data: TReq) => {
    try {
      return await fn(data);
    } catch (error) {
      throw mapFirebaseError(error);
    }
  };
}

function createMockCallable<TReq, TRes>(functionName: string) {
  return async (params: TReq): Promise<{ data: TRes }> => {
    // Small delay to simulate network call
    await new Promise((resolve) => setTimeout(resolve, 300));

    const result = await buildMockCloudFunctionResult(
      functionName,
      params as Record<string, unknown>
    );
    return result as { data: TRes };
  };
}
